/**
 * Primary bottom navigation — TASK 7.2 Steps 1, 2, 6.
 *
 * Extracted verbatim from mock/SteamyMusic.dc.html L703-711: a fixed,
 * backdrop-blurred bar of five equal `flex:1` destinations — four links and
 * the MORE button — each an icon glyph stacked over a 10px uppercase label.
 *
 *   <nav aria-label="Primary" style="position:fixed;…z-index:45;…">
 *     <div style="max-width:820px;margin:0 auto;display:flex;padding:6px 6px calc(6px + env(safe-area-inset-bottom))">
 *       <a href="#/"          aria-current="{{ navHomeCur }}"   style="…color:{{ navHomeCol }};min-height:44px">◆ HOME</a>
 *       …
 *       <button onClick="{{ moreTap }}" aria-haspopup="menu"    style="…">☰ MORE</button>
 *
 * -> `.sm-nav > .sm-nav__inner > .sm-nav__item(+--current)` with
 * `.sm-nav__icon` / `.sm-nav__label` children (styles/README §1).
 *
 * Three things TASK 7.2 adds to the mock:
 *   1. the ui-contract §3 testids (`nav-home-link` … `nav-more-button`);
 *   2. `aria-current="page"` is REMOVED, not set to the string "undefined" —
 *      the mock bound `aria-current="{{ navHomeCur }}"` where the value was
 *      `undefined` for inactive items (L961-964), which the dc-runtime turned
 *      into a literal attribute in some paths (AC 0.2 wants exactly one);
 *   3. the active colour (`{{ nav*Col }}` = #FFD337 / #B9AECB, mock L958) is a
 *      MODIFIER CLASS (`.sm-nav__item--current`), not a CSSOM assignment —
 *      it is state-driven but takes only two values, so it belongs in CSS.
 *
 * Hit area (W22): `.sm-nav__item` carries the mock's own `min-height:44px` and
 * each item is `flex:1` of a full-width bar, plus the shared `::after`
 * extender (components.css §3). Nothing here nests a `.sm-btn` inside an
 * `overflow:hidden` ancestor, so the extender is never clipped.
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { append, el } from '../components/primitives/dom.js';

/**
 * @typedef {'home'|'browse'|'search'|'faves'|'stats'|'more'} NavSection
 */

/**
 * The bottom-nav destinations (glyph, label, href). The mock's original five
 * (mock L705-709) plus STATS, promoted from the MORE sheet to a first-class
 * bottom-bar destination on request — the `/stats` route is unchanged, only its
 * reachability. `more` is a `<button>`, the rest are links.
 *
 * @type {ReadonlyArray<{
 *   section: NavSection,
 *   icon: string,
 *   label: string,
 *   href?: string,
 *   testId: string,
 * }>}
 */
export const NAV_ITEMS = Object.freeze([
  { section: 'home', icon: '◆', label: 'HOME', href: '#/', testId: TESTIDS.NAV_HOME_LINK },
  {
    section: 'browse',
    icon: '▦',
    label: 'BROWSE',
    href: '#/browse',
    testId: TESTIDS.NAV_BROWSE_LINK,
  },
  {
    section: 'search',
    icon: '⌕',
    label: 'SEARCH',
    href: '#/search',
    testId: TESTIDS.NAV_SEARCH_LINK,
  },
  {
    section: 'faves',
    icon: '♥',
    label: 'FAVES',
    href: '#/favorites',
    testId: TESTIDS.NAV_FAVES_LINK,
  },
  {
    section: 'stats',
    icon: '★',
    label: 'STATS',
    href: '#/stats',
    testId: TESTIDS.NAV_STATS_LINK,
  },
  { section: 'more', icon: '☰', label: 'MORE', testId: TESTIDS.NAV_MORE_BUTTON },
]);

/**
 * @typedef {object} PrimaryNavHandle
 * @property {HTMLElement} element the `<nav aria-label="Primary">`
 * @property {HTMLElement} moreButton the MORE trigger (the overlay invoker —
 *   focus returns here when the MORE menu closes)
 * @property {(section: NavSection|null) => void} setActiveSection
 */

/**
 * Builds (does not mount) the primary nav.
 *
 * @param {{ onMore: () => void }} options
 * @returns {PrimaryNavHandle}
 */
export function PrimaryNav(options) {
  const { onMore } = options;

  const nav = el('nav', { className: 'sm-nav', attrs: { 'aria-label': 'Primary' } });
  const inner = el('div', { className: 'sm-nav__inner' });

  /** @type {Map<NavSection, HTMLElement>} */
  const items = new Map();
  let moreButton = /** @type {HTMLElement|null} */ (null);

  for (const item of NAV_ITEMS) {
    const isLink = item.href !== undefined;
    const node = el(isLink ? 'a' : 'button', {
      className: 'sm-nav__item',
      testId: item.testId,
      attrs: isLink
        ? { href: /** @type {string} */ (item.href) }
        : { type: 'button', 'aria-haspopup': 'menu' },
      ...(isLink ? {} : { on: { click: () => onMore() } }),
    });
    append(
      node,
      el('span', { className: 'sm-nav__icon', text: item.icon, attrs: { 'aria-hidden': 'true' } }),
      el('span', { className: 'sm-nav__label', text: item.label }),
    );
    items.set(item.section, node);
    if (!isLink) moreButton = node;
    inner.append(node);
  }

  nav.append(inner);

  if (moreButton === null) throw new Error('PrimaryNav: the MORE button was not rendered');

  /** @param {NavSection|null} section */
  function setActiveSection(section) {
    for (const [key, node] of items) {
      const active = key === section;
      node.classList.toggle('sm-nav__item--current', active);
      if (active) node.setAttribute('aria-current', 'page');
      else node.removeAttribute('aria-current');
    }
  }

  return { element: nav, moreButton, setActiveSection };
}
